import { styled } from '@mui/material';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary } from 'shell/components/Accordion';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import RichTextArea from 'shell/components/RichTextArea';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import TextInput from 'shell/components/TextInput';
import { CUSTOM_CONTENT_WORD_COUNT_LIMIT } from 'utils/constants';
import { addCriterionListInstruction, removeCriterionListInstruction } from 'utils/gradingInstructions';
import { getHTMLWordCount } from 'utils/helper';

const LIST_CONFIG = {
    look_fors: {
        addLabel: 'Add Look-For',
        description: 'Concrete proof the AI can point to a specific phrase, moment, or match between slide and speech.',
        placeholder: 'Add another look-for',
        title: 'What would you see or hear if this were done well?',
        warning: false,
    },
    violations_omissions: {
        addLabel: 'Add Violation/Omission',
        description: 'Tell the AI what a student should not include or may have missed.',
        placeholder: 'Add a violation or omission',
        title: 'What does a violation or omission look like?',
        warning: true,
    },
};

const CriterionInstructions = ({
    criterion,
    error = {},
    instruction,
    onChange,
    onDraftChange,
    onListAdd,
    disabled = false,
    defaultExpanded = true,
}) => {
    const { t } = useTranslation('videoGrading');
    const [draft, setDraft] = useState({ listKey: '', value: '' });
    const value = {
        ...instruction,
        grading_instructions: instruction?.grading_instructions ?? null,
        look_fors: instruction?.look_fors || [],
        violations_omissions: instruction?.violations_omissions || [],
    };
    const latestInstruction = useRef({ disabled, onChange, value });
    useLayoutEffect(() => {
        latestInstruction.current = { disabled, onChange, value };
    });

    const beginAdd = (listKey) => {
        if (draft.value.trim()) return;
        setDraft({ listKey, value: '' });
    };
    const cancelAdd = () => {
        setDraft({ listKey: '', value: '' });
        onDraftChange?.(false);
    };
    const updateDraft = (nextValue) => {
        setDraft((current) => ({ ...current, value: nextValue }));
        onDraftChange?.(Boolean(nextValue.trim()));
    };
    const addDraft = () => {
        if (disabled) return;
        const next = addCriterionListInstruction(value, draft.listKey, draft.value);
        if (next === value) return;
        if (onListAdd?.(draft.listKey, next) === false) return;
        onChange(next);
        cancelAdd();
    };

    // RichTextArea can retain its callback after a list-only update. Merge into the
    // latest committed criterion so typing never restores an earlier list snapshot.
    const updateOverallInstruction = useCallback((grading_instructions) => {
        const current = latestInstruction.current;
        if (!current.disabled) current.onChange({ ...current.value, grading_instructions });
    }, []);

    return (
        <CriterionAccordion defaultExpanded={defaultExpanded} variant={'outlined'}>
            <CriterionAccordionSummary>
                <Stack spacing={2}>
                    <Text weight={'medium'}>{criterion.title}</Text>
                    {criterion.description && <Text color={'light'}>{criterion.description}</Text>}
                </Stack>
            </CriterionAccordionSummary>
            <CriterionAccordionDetails>
                <Stack spacing={5}>
                    {Object.entries(LIST_CONFIG).map(([listKey, config]) => {
                        const entries = value[listKey];
                        const isAdding = draft.listKey === listKey;
                        return (
                            <InstructionGroup key={listKey} $warning={config.warning} spacing={4}>
                                <Stack spacing={2}>
                                    <Text weight={'medium'}>{t(`criterion.${listKey}.title`, config.title)}</Text>
                                    <Text color={'light'}>
                                        {t(`criterion.${listKey}.description`, config.description)}
                                    </Text>
                                </Stack>
                                {entries.map((entry, index) => (
                                    <InstructionRow
                                        key={`${entry}-${index}`}
                                        alignItems={'center'}
                                        direction={'row'}
                                        spacing={2}
                                    >
                                        <Icon
                                            kind={config.warning ? 'error' : 'checkmark'}
                                            sx={(theme) => ({
                                                alignSelf: 'flex-start',
                                                marginTop: theme.spacing(1),
                                                color: theme.palette.ensightful[
                                                    config.warning ? 'error' : 'success'
                                                ][700],
                                            })}
                                        />
                                        <Text sx={{ flex: 1 }}>{entry}</Text>
                                        <IconButton
                                            disabled={disabled}
                                            aria-label={t('criterion.delete-entry', 'Delete instruction')}
                                            kind={'delete'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={() =>
                                                onChange(removeCriterionListInstruction(value, listKey, index))
                                            }
                                        />
                                    </InstructionRow>
                                ))}
                                {isAdding ? (
                                    <InlineAddForm spacing={3}>
                                        <TextInput
                                            disabled={disabled}
                                            autoFocus
                                            error={Boolean(error[listKey])}
                                            helperText={
                                                error[listKey]
                                                    ? t(
                                                          'assignment:error.instructions-too-long',
                                                          "Please make sure the instructions don't exceed {{wordCount}} words.",
                                                          { wordCount: CUSTOM_CONTENT_WORD_COUNT_LIMIT }
                                                      )
                                                    : undefined
                                            }
                                            placeholder={t(`criterion.${listKey}.placeholder`, config.placeholder)}
                                            value={draft.value}
                                            onChange={(event) => updateDraft(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    addDraft();
                                                }
                                                if (event.key === 'Escape') cancelAdd();
                                            }}
                                        />
                                        <Stack direction={'row'} justifyContent={'flex-end'} spacing={3}>
                                            <Button color={'neutral'} variant={'tertiary'} onClick={cancelAdd}>
                                                {t('controls.cancel', 'Cancel')}
                                            </Button>
                                            <Button disabled={disabled} onClick={addDraft}>
                                                {t('controls.add', 'Add')}
                                            </Button>
                                        </Stack>
                                    </InlineAddForm>
                                ) : (
                                    <Button
                                        disabled={disabled}
                                        sx={{ alignSelf: 'flex-start' }}
                                        startIcon={<Icon kind={'add'} />}
                                        variant={'borderless'}
                                        onClick={() => beginAdd(listKey)}
                                    >
                                        {t(`criterion.${listKey}.add`, config.addLabel)}
                                    </Button>
                                )}
                            </InstructionGroup>
                        );
                    })}
                    {value.grading_instructions === null ? (
                        <Button
                            disabled={disabled}
                            sx={{ alignSelf: 'flex-start' }}
                            startIcon={<Icon kind={'add'} />}
                            variant={'borderless'}
                            onClick={() => updateOverallInstruction('')}
                        >
                            {t('criterion.add-overall', 'Add Overall Criterion Instruction')}
                        </Button>
                    ) : (
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p2'}>
                                    {t('criterion.overall-label', 'Overall Criterion Instruction')}
                                </Text>
                                <IconButton
                                    disabled={disabled}
                                    aria-label={t('criterion.remove-overall', 'Remove overall criterion instruction')}
                                    kind={'delete'}
                                    size={'small'}
                                    variant={'square'}
                                    onClick={() => updateOverallInstruction(null)}
                                />
                            </Stack>
                            <RichTextArea
                                disabled={disabled}
                                error={Boolean(error.grading_instructions)}
                                helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                    wordLimitStatus: `${getHTMLWordCount(
                                        value.grading_instructions
                                    )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                                })}
                                helperTextPosition={'end'}
                                aria-label={t('criterion.overall-label', 'Overall Criterion Instruction')}
                                placeholder={t(
                                    'criterion.overall-placeholder',
                                    'Enter additional grading instructions for this criterion'
                                )}
                                value={value.grading_instructions}
                                onChange={updateOverallInstruction}
                            />
                        </Stack>
                    )}
                </Stack>
            </CriterionAccordionDetails>
        </CriterionAccordion>
    );
};

const CriterionAccordion = styled(Accordion)(({ theme }) => ({
    '&.MuiAccordion-root': {
        borderRadius: theme.spacing(2),
    },
}));

const CriterionAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    padding: theme.spacing(4),
    '& > .MuiAccordionSummary-content': {
        alignItems: 'flex-start',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
        marginTop: 0,
    },
}));

const CriterionAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(1, 4, 4),
}));

const InstructionGroup = styled(Stack, {
    shouldForwardProp: (prop) => prop !== '$warning',
})(({ $warning, theme }) => ({
    background: $warning ? theme.palette.ensightful.warning[50] : theme.palette.ensightful.neutral[50],
    borderRadius: theme.spacing(4),
    padding: theme.spacing(5),
}));

const InstructionRow = styled(Stack)(({ theme }) => ({
    background: theme.palette.ensightful.shades[0],
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
}));

const InlineAddForm = styled(Stack)(({ theme }) => ({
    background: theme.palette.ensightful.shades[0],
    borderRadius: theme.spacing(4),
    padding: theme.spacing(4),
}));

export default CriterionInstructions;
